import * as React from 'react';
import { connect } from 'react-redux';
import { getTranslate } from 'react-localize-redux';
import { withRouter, Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';

import { withStyles } from '@material-ui/core/styles';
import {
    Button, Typography, TextField, NoSsr, FormControlLabel, Checkbox, FormControl,
} from '@material-ui/core';
import classnames from 'classnames';

import * as AuthStore from '../../../app/store/AuthStore';
import urls from '../../../app/urls';
import {
    validateAllFields, validateField, validateRequired, validateEmail,
} from '../../../youwell-common/validationUtils';
import { contactEmail } from '../../../common/constants';
import { Loading } from '../../../youwell-common/components';

const styles = theme => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
    },
    header: {
        marginBottom: theme.spacing(1),
    },
    tabs: {
        marginBottom: theme.spacing(3),
    },
    registerDescription: {
        marginBottom: theme.spacing(2),
    },
    row: {
        display: 'flex',
    },
    textField: {
        marginBottom: theme.spacing(2),
    },
    schoolName: {
        flex: '1 1 auto',
        '&$hidden': {
            display: 'inline',
            visibility: 'hidden',
        },

    },
    privateUser: {
        flex: '1 1 50%',
        marginLeft: theme.spacing(2),
    },
    consentForm: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-around',

        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
        },
    },
    requiredCheckbox: {
        borderBottom: theme.borders.red,
    },
    actionButtons: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: theme.spacing(3),
    },
    infoContainer: {
        marginTop: theme.spacing(3),
    },
    loading: {
        height: 340,
        flex: '1 1 auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
    },
    linkToForgotPassword: {
        marginLeft: theme.spacing(1),
        color: theme.palette.primary.main,
        borderBottom: '1px solid',
    },
    hidden: {
        display: 'none',
    },
});

const defaultState = {
    name: '',
    email: '',
    phone: '',
    consent: false,
    schoolName: '',
    schoolRole: '',
    privateUser: false,
};

const validators = {
    name: validateRequired,
    email: [validateEmail, validateRequired],
    phone: validateRequired,
    consent: validateRequired,

    schoolName: validateRequired,
    schoolRole: validateRequired,
};

class RegisterUser extends React.Component {
    state = {
        ...defaultState,
        registrationSent: false,
        validationErrors: {},
    };

    handleChange = name => (event) => {
        this.setState({ [name]: event.target.value }, () => {
            if (validators[name]) {
                const validationErrors = validateField(this.state, validators[name], name); // eslint-disable-line react/no-access-state-in-setstate
                this.setState({ validationErrors });
            }
        });
    };

    handleCheckedChange = name => (event) => {
        this.setState({ [name]: event.target.checked }, () => {
            if (validators[name]) {
                const validationErrors = validateField(this.state, validators[name], name); // eslint-disable-line react/no-access-state-in-setstate
                this.setState({ validationErrors });
            }
        });
    };

    registerUser = () => {
        const selectedValidators = {
            email: validators.email,
            consent: validators.consent,
            name: validators.name,
            phone: validators.phone,
            schoolName: this.state.privateUser || validators.schoolName,
        };

        const validationErrors = validateAllFields(this.state, selectedValidators);

        if (validationErrors.hasErrors) {
            this.setState({ validationErrors });
            return;
        }

        const registerData = {
            organizationId: this.props.appConfig.orgId,
            name: this.state.name,
            email: this.state.email,
            phone: this.state.phone,
            extendedProperties: this.state.privateUser ? undefined : {
                schoolName: this.state.schoolName,
                schoolRole: this.state.schoolRole,
            },
        };

        const registerOptions = {
            callback: () => this.setState({ registrationSent: true }),
        };

        this.props.registerUser(registerData, registerOptions);
    };

    cancel = (e) => {
        e.preventDefault();
        window?.history?.back();
    }

    render() {
        const {
            classes, translate, errorMessage, loading, appConfig,
        } = this.props;

        if (this.state.registrationSent) {
            return (
                <div className={classes.root}>
                    <div className={classes.header}>
                        <Typography variant="h6">
                            {translate('register.registrationComplete')}
                        </Typography>
                    </div>

                    <Typography gutterBottom>
                        {translate('register.registrationCompleteDescription')}
                    </Typography>

                    <div className={classes.actionButtons}>
                        <div />
                        <Link to={urls.login}>
                            <Typography color="primary">
                                {translate('register.goToLogin')}
                            </Typography>
                        </Link>
                    </div>
                </div>
            );
        }

        return (
            <div className={classes.root}>
                <div className={classes.header}>
                    <Typography variant="h6">
                        {translate('register.user')}
                    </Typography>
                </div>

                <Loading loading={loading} />

                <Typography variant="body2">
                    {translate('register.registrationDescription')}
                </Typography>
                <Typography variant="body2" className={classes.registerDescription}>
                    {translate('register.registrationSchoolAffiliation')}
                </Typography>

                <TextField
                    id="name"
                    type="name"
                    label={translate('register.label.name')}
                    className={classnames(classes.textField)}
                    value={this.state.name}
                    onChange={this.handleChange('name')}
                    fullWidth
                    InputLabelProps={{
                        shrink: true,
                    }}
                    error={!!this.state.validationErrors.name}
                    helperText={this.state.validationErrors.name ? translate(this.state.validationErrors.name) : null}
                />
                <TextField
                    id="email"
                    type="email"
                    label={translate('register.label.email')}
                    className={classes.textField}
                    value={this.state.email}
                    onChange={this.handleChange('email')}
                    fullWidth
                    InputLabelProps={{
                        shrink: true,
                    }}
                    error={!!this.state.validationErrors.email}
                    helperText={this.state.validationErrors.email ? translate(this.state.validationErrors.email) : null}
                />
                <TextField
                    id="phone"
                    type="phone"
                    label={translate('register.label.phone')}
                    className={classnames(classes.textField)}
                    value={this.state.phone}
                    onChange={this.handleChange('phone')}
                    fullWidth
                    InputLabelProps={{
                        shrink: true,
                    }}
                    error={!!this.state.validationErrors.phone}
                    helperText={this.state.validationErrors.phone ? translate(this.state.validationErrors.phone) : translate('register.label.phoneDescription')}
                />
                <div className={classes.row}>
                    <TextField
                        id="schoolName"
                        type="schoolName"
                        label={translate('register.label.schoolName')}
                        className={classnames(classes.textField, classes.schoolName, { [classes.hidden]: this.state.privateUser })}
                        value={this.state.schoolName}
                        onChange={this.handleChange('schoolName')}
                        fullWidth
                        InputLabelProps={{
                            shrink: true,
                        }}
                        error={!!this.state.validationErrors.schoolName}
                        helperText={this.state.validationErrors.schoolName ? translate(this.state.validationErrors.schoolName) : translate('register.label.schoolNameDescription')}
                    />
                    {/* <FormControlLabel
                                className={classnames(classes.textField, classes.privateUser)}
                                control={(
                                    <Switch
                                        checked={this.state.privateUser}
                                        onChange={this.handleCheckedChange('privateUser')}
                                        value="privateUser"
                                        color="primary"
                                    />
                                )}
                                label={translate('register.registerAsPrivateUser')}
                            /> */}
                </div>
                <TextField
                    id="schoolRole"
                    type="schoolRole"
                    label={translate('register.label.schoolRole')}
                    className={classnames(classes.textField, { [classes.hidden]: this.state.privateUser })}
                    value={this.state.schoolRole}
                    onChange={this.handleChange('schoolRole')}
                    fullWidth
                    InputLabelProps={{
                        shrink: true,
                    }}
                    error={!!this.state.validationErrors.schoolRole}
                    helperText={this.state.validationErrors.schoolRole ? translate(this.state.validationErrors.schoolRole) : translate('register.label.roleDescription')}
                />
                <div className={classes.consentForm}>
                    <FormControl
                        error={!!this.state.validationErrors.consent}
                        className={this.state.validationErrors.consent ? classes.requiredCheckbox : null}
                    >
                        <FormControlLabel
                            control={(
                                <Checkbox
                                    checked={this.state.consent}
                                    onChange={this.handleCheckedChange('consent')}
                                    value="consent"
                                    color="primary"
                                />
                            )}
                            label={translate('register.consent')}
                        />
                    </FormControl>

                    <Typography variant="overline" align="center" color="primary">
                        <Link to={`${urls.page}/${appConfig.privacyPolicyTaskId}`}>
                            {translate('register.openPrivacyPolicy')}
                        </Link>
                    </Typography>
                </div>
                <div className={classes.actionButtons}>
                    <Link onClick={this.cancel} to={urls.home}>
                        <Typography color="primary">
                            {translate('register.cancel')}
                        </Typography>
                    </Link>
                    <Button onClick={this.registerUser} variant="contained" color="primary" disabled={loading}>
                        {translate('register.sendRegistration')}
                    </Button>
                </div>
                <div className={classes.infoContainer}>
                    {errorMessage && (
                        <NoSsr>
                            <Typography className={classes.error} color="error" align="center">
                                {translate(`errors.${errorMessage}`, { contactEmail }, { onMissingTranslation: ({ translationId }) => translationId })}
                                {errorMessage === '1035' && (
                                    <Link to={`${urls.login}/${urls.loginParts.forgotPassword}`} className={classes.linkToForgotPassword}>
                                        {translate('login.forgotPassword')}
                                    </Link>
                                )}
                            </Typography>
                        </NoSsr>
                    )}
                </div>
            </div>
        );
    }
}

RegisterUser.propTypes = {
    classes: PropTypes.object.isRequired,
    translate: PropTypes.func.isRequired,
    registerUser: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    errorMessage: PropTypes.string,
    appConfig: PropTypes.object.isRequired,
};

RegisterUser.defaultProps = {
    loading: false,
    errorMessage: null,
};

const mapStateToProps = state => ({
    translate: getTranslate(state.localize),
    loading: state.auth.loading,
    errorMessage: [].concat(state.auth.errors).filter(val => val).join('<br/>'),
    appConfig: state.pageSettings.appConfig,
});

const mapDispatchToProps = dispatch => ({
    ...bindActionCreators(AuthStore.actionCreators, dispatch),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(RegisterUser)));
